import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Spinner, Alert, Button } from 'react-bootstrap';
import api from '../../api/axios';
import AuthContext from '../../context/AuthContext';
import { isUserCancel, formatPgError } from '../../utils/pgError';

// 포트원 리다이렉트 복귀 지점.
// 모바일은 대부분 리디렉션 방식이라 requestIssueBillingKey 가 promise 로 돌아오지 않고
// redirectUrl 로 이동한다(생략 시 에러). PC(팝업)는 BillingPage 에서 promise 로 처리하므로
// 이 페이지를 거치지 않는다.
//
// 쿼리: ?transactionType=ISSUE_BILLING_KEY&billingKey=xxx
//       실패 시 code/message (+ PG 원문 pgCode/pgMessage)
const BillingCallbackPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { user } = useContext(AuthContext);
    const [stage, setStage] = useState('processing'); // processing | done | error
    const [error, setError] = useState('');

    useEffect(() => {
        const code = searchParams.get('code');
        const billingKey = searchParams.get('billingKey');

        // code 가 있으면 실패 — BillingPage 의 promise 분기와 동일 규약
        if (code) {
            const message = searchParams.get('message');
            // 사용자가 결제창을 닫은 것뿐이면 에러 화면 없이 구독 페이지로 돌려보낸다.
            if (isUserCancel(code, message)) {
                navigate('/account/billing', { replace: true });
                return;
            }
            setError(formatPgError(code, message, '카드 등록에 실패했습니다.'));
            setStage('error');
            return;
        }
        if (!billingKey) {
            setError('빌링키가 전달되지 않았습니다.');
            setStage('error');
            return;
        }
        // 수동 승인 건은 별도 API 가 필요해 현재 흐름에서 지원하지 않음
        if (billingKey === 'NEEDS_CONFIRMATION') {
            setError('추가 승인이 필요한 결제 수단입니다. 다른 카드로 시도해주세요.');
            setStage('error');
            return;
        }

        const register = async () => {
            try {
                await api.post('/billing/portone/billing-key', {
                    billingKey,
                    customerKey: `wn_shop_${user?.shopId}`,
                });
                setStage('done');
                setTimeout(() => navigate('/account/billing', { replace: true }), 1500);
            } catch (err) {
                // 서버가 내는 403/401 은 형태가 제각각이다:
                //   authenticateToken  → 평문 "Forbidden" (sendStatus)
                //   checkSubscription  → { error, message }
                //   라우트 핸들러       → { errors: [{ msg }] }
                // 전부 읽어야 원인이 화면에 드러난다. (예전엔 axios 기본 문구만 떠서 구분 불가)
                const d = err.response?.data;
                const detail = d?.errors?.[0]?.msg
                    || d?.message
                    || d?.error
                    || (typeof d === 'string' && d.trim() ? d.trim() : null)
                    || err.message
                    || '구독 등록에 실패했습니다.';
                const status = err.response?.status;
                setError(status ? `[${status}] ${detail}` : detail);
                setStage('error');
            }
        };
        register();
    }, [searchParams, navigate, user]);

    return (
        <Container className="py-5" style={{ maxWidth: 600, textAlign: 'center' }}>
            {stage === 'processing' && (
                <>
                    <Spinner animation="border" />
                    <p className="mt-3">카드 등록을 처리하는 중입니다...</p>
                </>
            )}
            {stage === 'done' && (
                <Alert variant="success">
                    결제 수단이 등록되었습니다! 잠시 후 구독 페이지로 이동합니다.
                </Alert>
            )}
            {stage === 'error' && (
                <>
                    <Alert variant="danger">{error}</Alert>
                    <Button variant="primary" onClick={() => navigate('/account/billing')}>
                        구독 페이지로
                    </Button>
                </>
            )}
        </Container>
    );
};

export default BillingCallbackPage;
